import React, { useEffect, useState, useCallback } from "react";
import { Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { useContent } from "../../context/ContentContext";

const up = async (file) => { const fd = new FormData(); fd.append("file", file); const r = await api.post("/admin/upload", fd); return r.data.url; };

function Thumb({ url, label, onReplace, onDelete }) {
  return (
    <div className="relative group rounded-lg overflow-hidden border border-stone-200" data-testid="img-tile">
      <img src={url} alt={label} className="h-28 w-full object-cover" />
      <div className="absolute inset-x-0 bottom-0 bg-black/55 text-white text-[10px] px-2 py-1 truncate">{label}</div>
      <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {onReplace && <label title="Replace" data-testid="img-replace" className="bg-white rounded-full p-1.5 text-forest shadow cursor-pointer"><Upload size={13} /><input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && onReplace(e.target.files[0])} /></label>}
        {onDelete && <button title="Delete" data-testid="img-delete" onClick={onDelete} className="bg-white rounded-full p-1.5 text-red-600 shadow"><Trash2 size={13} /></button>}
      </div>
    </div>
  );
}

function Section({ title, count, children }) {
  if (!count) return null;
  return (
    <div className="card-lux p-5">
      <h3 className="font-serif text-lg text-forest mb-3">{title} <span className="text-xs text-stone-400">({count})</span></h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">{children}</div>
    </div>
  );
}

export default function ImagesAdmin() {
  const { content, refresh } = useContent();
  const [rooms, setRooms] = useState([]);
  const [exps, setExps] = useState([]);
  const [media, setMedia] = useState([]);
  const load = useCallback(() => {
    api.get("/rooms").then((r) => setRooms(r.data)).catch(() => {});
    api.get("/experiences").then((r) => setExps(r.data)).catch(() => {});
    api.get("/admin/media").then((r) => setMedia(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const uc = async (patch) => { try { await api.put("/admin/content", patch); toast.success("Updated"); refresh(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const ur = async (id, patch) => { try { await api.patch(`/admin/rooms/${id}`, patch); toast.success("Updated"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const ue = async (id, patch) => { try { await api.patch(`/admin/experiences/${id}`, patch); toast.success("Updated"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const delMedia = async (id) => { if (!window.confirm("Delete this gallery image?")) return; try { await api.delete(`/admin/media/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  if (!content) return null;

  const heroImgs = content.hero_images || [];
  const welcome = content.welcome_images || [];
  const attractions = content.attractions || [];
  const facilities = content.facilities || [];
  const pageHeroes = content.page_heroes || {};
  const PAGES = [["accommodation", "Accommodation"], ["experiences", "Experiences"], ["gallery", "Gallery"], ["booking", "Booking"], ["contact", "Contact"]];

  return (
    <div className="space-y-6" data-testid="images-admin">
      <p className="text-sm text-stone-500">Every image used across the website in one place. Hover a tile to <strong>replace</strong> or <strong>delete</strong> it — changes save immediately.</p>

      <Section title="Brand logo" count={content.logo_url ? 1 : 0}>
        {content.logo_url && <Thumb url={content.logo_url} label="Logo" onReplace={async (f) => uc({ logo_url: await up(f) })} onDelete={() => uc({ logo_url: "" })} />}
      </Section>

      <Section title="Homepage banner" count={heroImgs.length}>
        {heroImgs.map((u, i) => <Thumb key={i} url={u} label={`Banner ${i + 1}`}
          onReplace={async (f) => { const nu = await up(f); const a = [...heroImgs]; a[i] = nu; uc({ hero_images: a }); }}
          onDelete={() => uc({ hero_images: heroImgs.filter((_, x) => x !== i) })} />)}
      </Section>

      <Section title="Page banners" count={PAGES.filter(([p]) => pageHeroes[p]?.image).length}>
        {PAGES.map(([p, label]) => pageHeroes[p]?.image ? <Thumb key={p} url={pageHeroes[p].image} label={`${label} banner`}
          onReplace={async (f) => { const nu = await up(f); uc({ page_heroes: { ...pageHeroes, [p]: { ...pageHeroes[p], image: nu } } }); }}
          onDelete={() => uc({ page_heroes: { ...pageHeroes, [p]: { ...pageHeroes[p], image: "" } } })} /> : null)}
      </Section>

      <Section title="Welcome section" count={welcome.filter((w) => w.url).length}>
        {welcome.map((w, i) => w.url ? <Thumb key={i} url={w.url} label={w.title || `Welcome ${i + 1}`}
          onReplace={async (f) => { const nu = await up(f); const a = [...welcome]; a[i] = { ...a[i], url: nu }; uc({ welcome_images: a }); }}
          onDelete={() => uc({ welcome_images: welcome.filter((_, x) => x !== i) })} /> : null)}
      </Section>

      <Section title="Homepage attractions" count={attractions.filter((a) => a.image).length}>
        {attractions.map((a, i) => a.image ? <Thumb key={i} url={a.image} label={a.title || `Attraction ${i + 1}`}
          onReplace={async (f) => { const nu = await up(f); const arr = [...attractions]; arr[i] = { ...arr[i], image: nu }; uc({ attractions: arr }); }}
          onDelete={() => { const arr = [...attractions]; arr[i] = { ...arr[i], image: "" }; uc({ attractions: arr }); }} /> : null)}
      </Section>

      {facilities.map((f, fi) => (f.images && f.images.length) ? (
        <Section key={fi} title={`Facility: ${f.label || "(unnamed)"}`} count={f.images.length}>
          {f.images.map((u, x) => <Thumb key={x} url={u} label={`${f.label} ${x + 1}`}
            onReplace={async (file) => { const nu = await up(file); const facs = facilities.map((ff, idx) => idx === fi ? { ...ff, images: ff.images.map((im, ix) => ix === x ? nu : im) } : ff); uc({ facilities: facs }); }}
            onDelete={() => { const facs = facilities.map((ff, idx) => idx === fi ? { ...ff, images: ff.images.filter((_, ix) => ix !== x) } : ff); uc({ facilities: facs }); }} />)}
        </Section>
      ) : null)}

      {rooms.map((r) => (r.images && r.images.length) ? (
        <Section key={r.id} title={`Room: ${r.name}`} count={r.images.length}>
          {r.images.map((u, x) => <Thumb key={x} url={u} label={`${r.name} ${x + 1}`}
            onReplace={async (file) => { const nu = await up(file); const imgs = r.images.map((im, ix) => ix === x ? nu : im); const patch = { images: imgs }; if (r.primary_image === u) patch.primary_image = nu; ur(r.id, patch); }}
            onDelete={() => { const imgs = r.images.filter((_, ix) => ix !== x); const patch = { images: imgs }; if (r.primary_image === u) patch.primary_image = imgs[0] || ""; ur(r.id, patch); }} />)}
        </Section>
      ) : null)}

      <Section title="Experiences" count={exps.filter((e) => e.image).length}>
        {exps.map((e) => e.image ? <Thumb key={e.id} url={e.image} label={e.title}
          onReplace={async (file) => ue(e.id, { image: await up(file) })}
          onDelete={() => ue(e.id, { image: "" })} /> : null)}
      </Section>

      <Section title="Gallery / media library" count={media.length}>
        {media.map((m) => <Thumb key={m.id} url={m.url} label={m.title || m.album} onDelete={() => delMedia(m.id)} />)}
      </Section>
    </div>
  );
}
